
import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Plus, Download, Trash } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";
import { PdfDownloader } from "./PdfDownloader";

interface GroceryItem {
  id: string;
  name: string;
  quantity?: string;
  completed: boolean;
}

interface GroceryListProps {
  title?: string;
  ingredients?: string[];
}

export function GroceryList({ title = "Grocery List", ingredients = [] }: GroceryListProps) {
  const { user } = useAuth();
  const [items, setItems] = useState<GroceryItem[]>([]);
  const [newItem, setNewItem] = useState("");
  const [newQuantity, setNewQuantity] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  
  useEffect(() => {
    if (user) {
      fetchGroceryItems();
    }
  }, [user]);
  
  useEffect(() => {
    // Add ingredients to grocery list if provided
    if (ingredients.length > 0) {
      const initialItems: GroceryItem[] = ingredients.map((ingredient, index) => {
        // Try to extract quantity from the ingredient string (e.g. "2 cups flour" -> "2 cups" and "flour")
        const match = ingredient.match(/^([\d\/\.\s]+\s*[a-zA-Z]+)\s+(.+)$/);
        if (match) {
          return {
            id: `temp-${index}`,
            name: match[2].trim(),
            quantity: match[1].trim(),
            completed: false
          };
        }
        return {
          id: `temp-${index}`,
          name: ingredient,
          completed: false
        };
      });
      
      setItems(initialItems);
    }
  }, [ingredients]);
  
  const fetchGroceryItems = async () => {
    if (!user) return;
    
    setIsLoading(true);
    try {
      const { data, error } = await supabase
        .from('grocery_items')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false });
        
      if (error) {
        console.error("Error fetching grocery items:", error);
        toast({
          title: "Error",
          description: "Failed to load grocery items",
          variant: "destructive"
        });
      }
      
      // Transform the database items to match our GroceryItem interface
      const transformedItems: GroceryItem[] = (data || []).map(item => ({
        id: item.id,
        name: item.item_name,
        quantity: item.quantity || undefined,
        completed: item.is_checked
      }));
      
      setItems(transformedItems);
    } finally {
      setIsLoading(false);
    }
  };
  
  const addItem = async () => {
    if (!newItem.trim() || !user) return;
    
    // First add to local state
    const tempId = `temp-${Date.now()}`;
    const newGroceryItem: GroceryItem = {
      id: tempId,
      name: newItem.trim(),
      quantity: newQuantity.trim() || undefined,
      completed: false
    };
    
    setItems(prevItems => [...prevItems, newGroceryItem]);
    setNewItem("");
    setNewQuantity("");
    
    // Then save to database
    try {
      const { data, error } = await supabase
        .from('grocery_items')
        .insert({
          user_id: user.id,
          item_name: newItem.trim(),
          quantity: newQuantity.trim() || null,
          is_checked: false
        })
        .select();
        
      if (error) {
        console.error("Error adding grocery item:", error);
        toast({
          title: "Error",
          description: "Failed to save grocery item",
          variant: "destructive"
        });
        // Remove the temp item if there was an error
        setItems(prevItems => prevItems.filter(item => item.id !== tempId));
      }
      
      if (data && data.length > 0) {
        // Update the item with the real ID from the database
        setItems(prevItems => 
          prevItems.map(item => 
            item.id === tempId 
              ? {
                  id: data[0].id,
                  name: data[0].item_name,
                  quantity: data[0].quantity || undefined,
                  completed: data[0].is_checked
                }
              : item
          )
        );
      }
    } catch (error) {
      console.error("Error adding grocery item:", error);
    }
  };
  
  const toggleItem = async (id: string) => {
    // First update local state
    setItems(prevItems => 
      prevItems.map(item => 
        item.id === id 
          ? { ...item, completed: !item.completed }
          : item
      )
    );
    
    // Then update in database
    if (id.startsWith('temp-')) return; // Skip temporary items
    
    try {
      const itemToUpdate = items.find(item => item.id === id);
      
      if (!itemToUpdate) return;
      
      const { error } = await supabase
        .from('grocery_items')
        .update({
          is_checked: !itemToUpdate.completed
        })
        .eq('id', id);
        
      if (error) {
        console.error("Error updating grocery item:", error);
        // Revert the local change if there was an error
        setItems(prevItems => 
          prevItems.map(item => 
            item.id === id 
              ? { ...item, completed: !item.completed }
              : item
          )
        );
      }
    } catch (error) {
      console.error("Error updating grocery item:", error);
    }
  };
  
  const deleteItem = async (id: string) => {
    // First update local state
    setItems(prevItems => prevItems.filter(item => item.id !== id));
    
    // Then delete from database
    if (id.startsWith('temp-')) return; // Skip temporary items
    
    try {
      const { error } = await supabase
        .from('grocery_items')
        .delete()
        .eq('id', id);
        
      if (error) {
        console.error("Error deleting grocery item:", error);
        // Fetch all items again if there was an error
        fetchGroceryItems();
      }
    } catch (error) {
      console.error("Error deleting grocery item:", error);
    }
  };
  
  const saveAllItems = async () => {
    if (!user) return;
    
    // Filter out items that start with 'temp-' (these are already in the list from ingredients)
    const tempItems = items.filter(item => item.id.startsWith('temp-'));
    
    if (tempItems.length === 0) {
      toast({
        title: "Info",
        description: "No new items to add to your grocery list"
      });
      return;
    }
    
    try {
      const { error } = await supabase
        .from('grocery_items')
        .insert(
          tempItems.map(item => ({
            user_id: user.id,
            item_name: item.name,
            quantity: item.quantity || null,
            is_checked: item.completed
          }))
        );
        
      if (error) {
        console.error("Error saving grocery items:", error);
        toast({
          title: "Error",
          description: "Failed to save grocery items",
          variant: "destructive"
        });
      } else {
        toast({
          title: "Success",
          description: "All items saved to your grocery list"
        });
        fetchGroceryItems();
      }
    } catch (error) {
      console.error("Error saving grocery items:", error);
    }
  };
  
  return (
    <div className="border rounded-lg p-4 mt-4">
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-medium">{title}</h3>
        <div className="flex gap-2">
          {ingredients.length > 0 && (
            <Button 
              variant="outline" 
              size="sm"
              onClick={saveAllItems}
            >
              <Plus className="h-3.5 w-3.5 mr-1" />
              Save to My List
            </Button>
          )}
          
          <PdfDownloader 
            type="groceryList"
            title={title}
            content={items.map(item => item.quantity ? `${item.quantity} ${item.name}` : item.name)}
          />
        </div>
      </div>
      
      <div className="space-y-2 max-h-[300px] overflow-y-auto mb-4">
        {items.length === 0 ? (
          <div className="text-center text-muted-foreground py-4">
            No items in the list
          </div>
        ) : (
          items.map((item) => (
            <div key={item.id} className="flex items-center justify-between group">
              <div className="flex items-start gap-2">
                <Checkbox 
                  id={`item-${item.id}`}
                  checked={item.completed}
                  onCheckedChange={() => toggleItem(item.id)}
                  className="mt-1"
                />
                <div className="flex flex-col">
                  <label 
                    htmlFor={`item-${item.id}`}
                    className={`cursor-pointer ${item.completed ? 'line-through text-muted-foreground' : ''}`}
                  >
                    {item.name}
                  </label>
                  {item.quantity && (
                    <span className="text-xs text-muted-foreground">
                      {item.quantity}
                    </span>
                  )}
                </div>
              </div>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => deleteItem(item.id)}
                className="h-7 w-7 opacity-0 group-hover:opacity-100 transition-opacity"
              >
                <Trash className="h-3.5 w-3.5" />
              </Button>
            </div>
          ))
        )}
      </div>
      
      <div className="flex gap-2 mt-4">
        <Input
          placeholder="Item"
          value={newItem}
          onChange={(e) => setNewItem(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && addItem()}
          className="flex-grow"
        />
        <Input
          placeholder="Quantity"
          value={newQuantity}
          onChange={(e) => setNewQuantity(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && addItem()}
          className="w-1/3"
        />
        <Button onClick={addItem}>
          <Plus className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
}
